import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Spinner } from './EnvLayout';
import { ui, errText } from './envUi';
import { hasGameFile } from './gameRegistry';

/** Rename, reorder and show/hide the buttons on the hub. */
export default function AdminGames() {
  const utils = trpc.useUtils();
  const { data: games, isLoading, error } = trpc.environmental.adminGames.useQuery();
  const refresh = () => { utils.environmental.adminGames.invalidate(); utils.environmental.games.invalidate(); };

  const update  = trpc.environmental.updateGame.useMutation({ onSuccess: refresh });
  const reorder = trpc.environmental.reorderGames.useMutation({ onSuccess: refresh });
  const [titles, setTitles] = useState<Record<number, string>>({});

  if (isLoading) return <Spinner />;
  if (error || !games) return <p className="text-red-600 dark:text-red-400">{error ? errText(error) : 'Could not load games.'}</p>;

  const move = (id: number, dir: -1 | 1) => {
    const ids = games.map(g => g.id);
    const i = ids.indexOf(id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate({ orderedIds: ids });
  };

  return (
    <section>
      <p className={`${ui.muted} mb-4`}>
        These are the buttons on the games page. Change a name, move it up or down, or hide it.
      </p>
      <ul className="flex flex-col gap-3">
        {games.map((g, idx) => {
          const draft = titles[g.id] ?? g.title;
          const dirty = draft.trim() !== g.title && draft.trim() !== '';
          return (
            <li key={g.id} className={`${ui.card} flex flex-wrap items-center gap-3 p-3`}>
              <div className="flex flex-col">
                <button aria-label="Move up" disabled={idx === 0 || reorder.isPending} onClick={() => move(g.id, -1)}
                        className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▲</button>
                <button aria-label="Move down" disabled={idx === games.length - 1 || reorder.isPending} onClick={() => move(g.id, 1)}
                        className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▼</button>
              </div>

              <div className="min-w-[10rem] flex-1">
                <input className={ui.input} value={draft} aria-label={`Name of ${g.slug}`}
                       onChange={e => setTitles(t => ({ ...t, [g.id]: e.target.value }))} />
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                  {g.slug}{!hasGameFile(g.slug) && ' · no game file yet (shows "coming soon")'}
                </p>
              </div>

              {dirty && (
                <button className={ui.btn} disabled={update.isPending}
                        onClick={() => update.mutate({ id: g.id, title: draft.trim() }, {
                          onSuccess: () => setTitles(t => { const n = { ...t }; delete n[g.id]; return n; }),
                        })}>
                  Save name
                </button>
              )}
              <button className={ui.btnGhost} disabled={update.isPending}
                      onClick={() => update.mutate({ id: g.id, visible: !g.visible })}>
                {g.visible ? 'Hide' : 'Show'}
              </button>
              {g.visible && <Link to={`/environmental/${g.slug}`} className={ui.btnGhost}>Open</Link>}
              {!g.visible && <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400">Hidden</span>}
            </li>
          );
        })}
      </ul>
      {(update.error || reorder.error) && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{errText(update.error ?? reorder.error)}</p>}
    </section>
  );
}
